import React, { useState, useEffect, useRef } from 'react';
import { Grid3X3, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MegaCategoryDropdown = ({ onCategorySelect, onViewAll }) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/products/categories`);
        if (res.ok) {
          const data = await res.json();
          setCategories(data.filter(c => c.is_active !== false));
        }
      } catch {}
    };
    fetchCategories();
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSelect = (cat) => {
    setOpen(false);
    onCategorySelect?.(cat.id, cat.slug);
  };

  // Split categories into columns (4 columns)
  const colCount = 4;
  const perCol = Math.ceil(categories.length / colCount);
  const columns = Array.from({ length: colCount }, (_, i) =>
    categories.slice(i * perCol, (i + 1) * perCol)
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        onDoubleClick={() => { setOpen(false); onViewAll?.(); }}
        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          open
            ? 'bg-teal-600 text-white'
            : 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/50'
        }`}
        data-testid="mega-category-btn"
        title="Click to browse, double-click for all products"
      >
        <Grid3X3 className="w-4 h-4" />
        All Categories
      </button>

      {open && categories.length > 0 && (
        <div
          className="absolute top-full left-0 mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 z-50 min-w-[700px] p-6"
          data-testid="mega-category-panel"
        >
          <div className="grid grid-cols-4 gap-x-8 gap-y-1">
            {columns.map((col, ci) => (
              <div key={ci} className="space-y-0.5">
                {col.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => handleSelect(cat)}
                    className="w-full text-left px-2 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-md transition-colors flex items-center justify-between group"
                    data-testid={`mega-cat-${cat.slug}`}
                  >
                    <span>{cat.name}</span>
                    <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-teal-500" />
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MegaCategoryDropdown;
